import type { StaffRole } from "@luckymenu/core";
import { createStore } from "zustand/vanilla";
import type { KeyValueStorage } from "../adapters";

/**
 * A quick switch on a shared device (P6-T11): the member acting now, in one
 * restaurant, until `expiresAt`. Kept on the device, so a reload or restart
 * keeps the person who switched in (the Android app's background Accept
 * reads it too, `STAFF_SWITCH_KEY`).
 */
export type ActiveSwitch = {
	restaurantId: string;
	token: string;
	userId: string;
	name: string | null;
	role: StaffRole;
	expiresAt: string;
};

export type StaffSwitchState = {
	active: ActiveSwitch | null;
	set(active: ActiveSwitch | null): void;
};

export const STAFF_SWITCH_KEY = "luckymenu.staffSwitch";

/** The switch's token for calls to `restaurantId`, or null when it is for another or has run out. */
export function switchTokenFor(
	active: ActiveSwitch | null,
	restaurantId: string | null,
	now: number,
): string | null {
	if (!active || !restaurantId || active.restaurantId !== restaurantId) return null;
	return Date.parse(active.expiresAt) > now ? active.token : null;
}

export function parseStoredSwitch(raw: string | null): ActiveSwitch | null {
	if (!raw) return null;
	try {
		const value = JSON.parse(raw) as ActiveSwitch;
		return typeof value.token === "string" && typeof value.restaurantId === "string" ? value : null;
	} catch {
		return null;
	}
}

export function createStaffSwitchStore(storage: KeyValueStorage) {
	const store = createStore<StaffSwitchState>((set) => ({
		active: null,
		set(active) {
			set({ active });
			void (
				active
					? storage.setItem(STAFF_SWITCH_KEY, JSON.stringify(active))
					: storage.removeItem(STAFF_SWITCH_KEY)
			).catch(() => {});
		},
	}));
	void storage
		.getItem(STAFF_SWITCH_KEY)
		.catch(() => null)
		.then((raw) => {
			const saved = parseStoredSwitch(raw);
			if (saved && store.getState().active === null) store.setState({ active: saved });
		});
	return store;
}

export type StaffSwitchStore = ReturnType<typeof createStaffSwitchStore>;
